import React from 'react'
import { Droppable, Draggable } from '@hello-pangea/dnd'
import { useActiveRecipes } from '../../hooks/useActiveRecipes'
import { t } from '../../i18n'
import { RecipeStatus } from '../recipes/types'

interface RecipeListProps {
  searchInput: string
  excludedRecipeIds: number[]
}

interface Recipe {
  id: number
  name: string
  portion_type: string
  processing_loss: number
  price: string
  clean_at: number
  image_url: string
  status: RecipeStatus
  created_at: string
  updated_at: string
  menu_category_name: string
}

const RecipeCard = ({ recipe }: { recipe: Recipe }) => {
  return (
    <div className="recipe-card">
      <div className="recipe-card-content">
        <div className="recipe-image">
          {recipe.image_url ? (
            <img src={recipe.image_url} alt={recipe.name} />
          ) : (
            <div className="recipe-image-placeholder">
              <i className="fa fa-cutlery"></i>
            </div>
          )}
        </div>
        <div className="recipe-details">
          <h3 className="recipe-name">{recipe.name}</h3>
        </div>
      </div>
    </div>
  )
}


const RecipeList = ({ searchInput, excludedRecipeIds }: RecipeListProps) => {
  const { recipes, sentinelRef, rootRef, loading, hasMoreRecordsInScope, error } =
    useActiveRecipes<Recipe>({ query: searchInput })

  const visibleRecipes = recipes.filter(recipe => !excludedRecipeIds.includes(recipe.id))
  const showInitialLoading = loading && recipes.length === 0

  return (
    <div className="recipes-content" ref={rootRef}>
      {showInitialLoading && (
        <div className="recipes-initial-loading d-flex justify-content-center py-4">
          <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
        </div>
      )}
      {error && !loading && recipes.length === 0 && (
        <p className="text-danger px-2">{t('recipes.load_error')}</p>
      )}
      <Droppable droppableId="recipes-list" isDropDisabled={true}>
        {(provided) => (
          <div
            className="recipes-list"
            ref={provided.innerRef}
            {...provided.droppableProps}
          >
            {visibleRecipes.map((recipe, index) => (
              <Draggable
                key={recipe.id}
                draggableId={JSON.stringify({ id: recipe.id, name: recipe.name, image_url: recipe.image_url })}
                index={index}
              >
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`draggable-recipe ${snapshot.isDragging ? 'is-dragging' : ''}`}
                  >
                    <RecipeCard recipe={recipe} />
                  </div>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
      {hasMoreRecordsInScope && <div ref={sentinelRef} aria-hidden="true" style={{ height: 1, overflowAnchor: 'none' }} />}
      {loading && recipes.length > 0 && (
        <div className="recipes-loading-more d-flex justify-content-center py-2" style={{ overflowAnchor: 'none' }}>
          <span className="spinner-border spinner-border-sm ms-2" role="status" aria-hidden="true" />
        </div>
      )}
    </div>
  )
}


export default RecipeList
